import api from "@/lib/api";
import { viewFromApi } from "@/components/FileViewer";
import { FileText, FileSpreadsheet, Presentation, Image as ImageIcon, FileArchive, File, FileType } from "lucide-react";

export const MAX_BYTES = 25 * 1024 * 1024;
export const ACCEPT = ".pdf,.doc,.docx,.xls,.xlsx,.csv,.ppt,.pptx,.txt,.rtf,.odt,.ods,.odp,.png,.jpg,.jpeg,.webp,.gif,.heic,.zip";

export const fmtBytes = (b) => {
  if (!b) return "0 MB";
  const units = [["TB", 1024 ** 4], ["GB", 1024 ** 3], ["MB", 1024 ** 2], ["KB", 1024]];
  for (const [u, d] of units) if (b >= d) { const v = b / d; return `${v >= 100 ? Math.round(v) : Number(v.toFixed(1))} ${u}`; }
  return `${b} B`;
};
export const fmtSize = (b) => (!b ? "—" : b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
export const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "");

export function fileIcon(ext) {
  const e = (ext || "").toLowerCase();
  if (e === "pdf") return { Icon: FileText, cls: "text-red-600 bg-red-50" };
  if (["doc", "docx", "odt", "rtf"].includes(e)) return { Icon: FileType, cls: "text-blue-700 bg-blue-50" };
  if (["xls", "xlsx", "csv", "ods"].includes(e)) return { Icon: FileSpreadsheet, cls: "text-emerald-700 bg-emerald-50" };
  if (["ppt", "pptx", "odp"].includes(e)) return { Icon: Presentation, cls: "text-orange-600 bg-orange-50" };
  if (["png", "jpg", "jpeg", "webp", "gif", "heic", "heif"].includes(e)) return { Icon: ImageIcon, cls: "text-violet-600 bg-violet-50" };
  if (e === "zip") return { Icon: FileArchive, cls: "text-amber-700 bg-amber-50" };
  return { Icon: File, cls: "text-slate-600 bg-slate-100" };
}

export const canPreview = (ext) => ["pdf", "png", "jpg", "jpeg", "webp", "gif", "txt", "doc", "docx", "docm", "dot", "xls", "xlsx", "xlsm", "xlt", "ppt", "pptx", "pps", "pot", "csv", "heic", "heif"].includes((ext || "").toLowerCase());

export function openDoc(file) {
  return viewFromApi(api, `/docs/files/${file.id}`, { title: file.name, filename: file.name, type: file.mime });
}

export async function downloadDoc(file) {
  const res = await api.get(`/docs/files/${file.id}`, { params: { download: 1 }, responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url; a.download = file.name; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/** Chunked upload: init → PUT chunks → complete. onProgress(0..1). */
export async function uploadDoc({ scope, folderId, file, onProgress }) {
  if (file.size > MAX_BYTES) throw new Error(`${file.name}: too large (max 25 MB).`);
  const { data: init } = await api.post("/docs/uploads/init", { scope, folder_id: folderId || null, name: file.name, size: file.size });
  const chunk = init.chunk_bytes;
  for (let off = 0; off < file.size; off += chunk) {
    const blob = file.slice(off, Math.min(off + chunk, file.size));
    await api.put(`/docs/uploads/${init.upload_id}/chunk`, blob, { headers: { "Content-Type": "application/octet-stream" } });
    onProgress?.(Math.min(1, (off + blob.size) / file.size));
  }
  const { data } = await api.post(`/docs/uploads/${init.upload_id}/complete`);
  return data;
}
